import { emptyField, newId, type ContactsSection } from '@sonder/shared'
import type { Mode } from '@/app/mode'

export type Contact = ContactsSection['items'][string]

/** A new contact's document. Values given here count as entered by whoever is editing (prefilled for staff). */
export function newContact(init: { name?: string; role?: string; phone?: string; email?: string; topics?: string[]; locationIds?: string[] }, mode: Mode): Contact {
  const now = Date.now()
  const isClient = mode === 'client'
  const field = (v = '') => (v ? { ...emptyField(v, now), prefilled: !isClient, source: isClient ? ('client' as const) : ('staff' as const), confirmed: isClient } : emptyField('', now))
  return { name: field(init.name), role: field(init.role), phone: field(init.phone), email: field(init.email), topics: init.topics ?? [], locationIds: init.locationIds ?? [], createdAt: now }
}
export const newContactId = () => newId().slice(0, 8)

/** Contacts covering every brand (or none in particular) are listed in their own group after the brands. */
export const SHARED_CONTACTS = 'shared'

/**
 * Which group a contact is listed under on Contact information: the brand whose locations it covers, or
 * SHARED_CONTACTS when it covers every location or spans brands. With one brand everything is that brand's.
 */
export function contactGroup(c: Pick<Contact, 'locationIds'>, brandIds: string[], brandOf: (locationId: string) => string | undefined): string {
  if (brandIds.length <= 1) return brandIds[0] ?? SHARED_CONTACTS
  const owners = new Set((c.locationIds ?? []).map(brandOf))
  return owners.size === 1 ? ([...owners][0] ?? SHARED_CONTACTS) : SHARED_CONTACTS
}

/** Navigation state that opens Contact information on one brand's group (or the shared one). */
export type ContactsNavState = { focusContact?: string; focusGroup?: string }

/** Scrolls to a group of contacts on Contact information. */
export function focusContactGroup(group: string) {
  requestAnimationFrame(() => document.querySelector<HTMLElement>(`[data-contact-group="${CSS.escape(group)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

/** Scrolls to a contact card and puts the cursor in its phone number. */
export function focusContact(id: string) {
  requestAnimationFrame(() => {
    const el = document.querySelector<HTMLElement>(`[data-contact="${CSS.escape(id)}"]`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    el?.querySelector<HTMLInputElement>('[data-field$=".phone"] input')?.focus({ preventScroll: true })
  })
}
